// PWA Push Notification Service
class PWANotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  async initialize() {
    if (!this.isSupported) {
      console.log('Push notifications are not supported in this browser');
      return false;
    }

    // Check if we're running over HTTPS or localhost
    const isSecureContext = window.isSecureContext || window.location.hostname === 'localhost';
    if (!isSecureContext) {
      console.warn('PWA push notifications require HTTPS or localhost. Current protocol:', window.location.protocol);
      console.warn('To enable push notifications on mobile devices, you need HTTPS.');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Check if we already have a subscription
      this.subscription = await this.registration.pushManager.getSubscription();
      
      return true;
    } catch (error) {
      console.error('Failed to register service worker:', error);
      console.error('This is likely because you need HTTPS for PWA push notifications on mobile devices');
      return false;
    }
  }

  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      return true;
    } else {
      console.log('Notification permission denied');
      return false;
    }
  }

  async subscribeToPushNotifications() {
    if (!this.isSupported || !this.registration) {
      throw new Error('Service worker not registered');
    }

    try {
      // Request permission first
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        throw new Error('Notification permission denied');
      }

      // Create push subscription
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.getVapidPublicKey())
      });

      console.log('Push subscription created:', this.subscription);

      // Send subscription to server
      await this.sendSubscriptionToServer(this.subscription);
      
      return this.subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  async unsubscribeFromPushNotifications() {
    if (!this.subscription) {
      return true;
    }

    try {
      const result = await this.subscription.unsubscribe();
      this.subscription = null;
      
      // Notify server about unsubscription
      await this.removeSubscriptionFromServer();
      
      console.log('Unsubscribed from push notifications');
      return result;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw error;
    }
  }

  async sendSubscriptionToServer(subscription) {
    try {
      // Use the same API configuration as the main app
      const baseUrl = process.env.REACT_APP_API_URL || 'https://api.paragonwheelapp.com';
      const response = await fetch(`${baseUrl}/notifications/register-device/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          device_token: subscription.endpoint,
          device_type: 'pwa',
          subscription_data: subscription.toJSON()
        })
      });

      if (response.ok) {
        console.log('Device registered successfully');
        return true;
      } else {
        throw new Error('Failed to register device');
      }
    } catch (error) {
      console.error('Error registering device:', error);
      throw error;
    }
  }

  async removeSubscriptionFromServer() {
    try {
      // Use the same API configuration as the main app
      const baseUrl = process.env.REACT_APP_API_URL || 'https://api.paragonwheelapp.com';
      const response = await fetch(`${baseUrl}/notifications/register-device/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        console.log('Device unregistered successfully');
        return true;
      }
    } catch (error) {
      console.error('Error unregistering device:', error);
    }
  }

  async checkSubscriptionStatus() {
    if (!this.isSupported || !this.registration) {
      return { subscribed: false, supported: false };
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      return {
        subscribed: !!subscription,
        supported: true,
        subscription: subscription
      };
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return { subscribed: false, supported: false };
    }
  }

  // Utility function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Get VAPID public key
  getVapidPublicKey() {
    // Your actual VAPID public key
    return 'BHAIBD3fWHCS3F3rXEsn8DYVuCiE3G7ISI3Q9v61823jOY7y9sLRlaLrbI4T9gjjGFTjtnmU5Hytvcda4Am1CcM';
  }

  // Show local notification (for testing)
  async showLocalNotification(title, body, data = {}) {
    if (!this.isSupported || !this.registration) {
      console.log('Cannot show notification - not supported or not registered');
      return;
    }

    const options = {
      body: body,
      icon: '/logo192.png',
      badge: '/favicon.ico',
      data: data,
      vibrate: [200, 100, 200],
      tag: 'paragon-notification',
      renotify: true,
      requireInteraction: true
    };

    await this.registration.showNotification(title, options);
  }
}

// Create singleton instance
const pwaNotificationService = new PWANotificationService();

export default pwaNotificationService;
